import React, { useEffect, useMemo, useState } from 'react';

/**
 * Animated tag-graph visual for the hero.
 * Nodes are tags; edges are learned relationships.
 * A pulse travels from a "seed" tag through neighbors, showing bounded propagation.
 */
function TagGraph() {
  const [pulseKey, setPulseKey] = useState(0);
  const [activeIdx, setActiveIdx] = useState(0);

  // Graph data — 12 nodes, positioned within 480x480 viewBox.
  const nodes = useMemo(
    () => [
      { id: 'auth', label: 'auth', x: 240, y: 240, seed: true },
      { id: 'oauth2', label: 'oauth2', x: 130, y: 160 },
      { id: 'jwt', label: 'jwt', x: 350, y: 150 },
      { id: 'session', label: 'session', x: 110, y: 320 },
      { id: 'refresh', label: 'refresh-tok', x: 380, y: 300 },
      { id: 'google', label: 'google-sso', x: 60, y: 100 },
      { id: 'cookie', label: 'cookie', x: 200, y: 380 },
      { id: 'bearer', label: 'bearer', x: 420, y: 210 },
      { id: 'scope', label: 'scope', x: 300, y: 80 },
      { id: 'revoke', label: 'revoke', x: 420, y: 380 },
      { id: 'pkce', label: 'pkce', x: 60, y: 240 },
      { id: 'csrf', label: 'csrf', x: 180, y: 70 },
    ],
    []
  );

  // Edges — the "learned graph". weight = edge strength (0.2–1.0).
  const edges = useMemo(
    () => [
      ['auth', 'oauth2', 0.95],
      ['auth', 'jwt', 0.9],
      ['auth', 'session', 0.8],
      ['auth', 'refresh', 0.7],
      ['auth', 'cookie', 0.55],
      ['auth', 'pkce', 0.5],
      ['oauth2', 'google', 0.85],
      ['oauth2', 'scope', 0.75],
      ['oauth2', 'pkce', 0.6],
      ['jwt', 'bearer', 0.9],
      ['jwt', 'scope', 0.5],
      ['jwt', 'revoke', 0.45],
      ['refresh', 'revoke', 0.7],
      ['session', 'cookie', 0.8],
      ['session', 'csrf', 0.6],
      ['csrf', 'cookie', 0.45],
    ],
    []
  );

  // Cycle through a small set of "seeds" to keep the visual alive.
  const seedCycle = useMemo(() => ['auth', 'oauth2', 'jwt', 'session'], []);

  useEffect(() => {
    const t = setInterval(() => {
      setPulseKey((k) => k + 1);
      setActiveIdx((i) => (i + 1) % seedCycle.length);
    }, 3200);
    return () => clearInterval(t);
  }, [seedCycle.length]);

  const activeSeed = seedCycle[activeIdx];
  const nodeById = useMemo(
    () => Object.fromEntries(nodes.map((n) => [n.id, n])),
    [nodes]
  );

  // Compute tiers: depth 0 = seed, depth 1 = direct neighbors, depth 2 = neighbors-of-neighbors.
  const neighborsOf = (id) =>
    edges
      .filter(([a, b]) => a === id || b === id)
      .map(([a, b, w]) => [a === id ? b : a, w]);

  const depth1 = neighborsOf(activeSeed);
  const depth1Ids = new Set(depth1.map(([id]) => id));
  const depth2 = [];
  depth1.forEach(([id]) => {
    neighborsOf(id).forEach(([id2, w]) => {
      if (id2 !== activeSeed && !depth1Ids.has(id2)) {
        depth2.push([id2, w]);
      }
    });
  });
  const depth2Ids = new Set(depth2.map(([id]) => id));

  const tier = (id) => {
    if (id === activeSeed) return 0;
    if (depth1Ids.has(id)) return 1;
    if (depth2Ids.has(id)) return 2;
    return 3;
  };

  return (
    <div className="graph-card">
      <span className="corner tl">tag-graph</span>
      <span className="corner tr">D=2 · M=32</span>
      <span className="corner bl">seed: {activeSeed}</span>
      <span className="corner br">bounded</span>

      <svg
        viewBox="0 0 480 480"
        width="100%"
        height="100%"
        style={{ position: 'absolute', inset: 0 }}
      >
        <defs>
          <filter id="nodeGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="6" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <radialGradient id="seedGlow" cx="50%" cy="50%" r="50%">
            <stop
              offset="0%"
              stopColor="oklch(0.72 0.14 60)"
              stopOpacity="0.35"
            />
            <stop
              offset="100%"
              stopColor="oklch(0.72 0.14 60)"
              stopOpacity="0"
            />
          </radialGradient>
        </defs>

        {/* edges */}
        {edges.map(([a, b, w], i) => {
          const na = nodeById[a];
          const nb = nodeById[b];
          if (!na || !nb) return null;
          const ta = tier(a);
          const tb = tier(b);
          const active = (ta <= 1 && tb <= 1) || (ta <= 2 && tb <= 2);
          const strong = a === activeSeed || b === activeSeed;
          const opacity = strong ? 0.95 : active ? 0.35 : 0.12;
          const stroke = strong ? 'oklch(0.42 0.14 60)' : '#0A0A0A';
          return (
            <line
              key={i}
              x1={na.x}
              y1={na.y}
              x2={nb.x}
              y2={nb.y}
              stroke={stroke}
              strokeWidth={strong ? 1.1 + w * 0.6 : 0.7}
              opacity={opacity}
              style={{ transition: 'opacity .6s, stroke .6s' }}
            />
          );
        })}

        {/* propagation pulses — travel from seed to depth-1 neighbors */}
        {depth1.map(([id], i) => {
          const a = nodeById[activeSeed];
          const b = nodeById[id];
          if (!a || !b) return null;
          return (
            <circle
              key={`p1-${pulseKey}-${i}`}
              r="3.5"
              fill="oklch(0.72 0.14 60)"
            >
              <animate
                attributeName="cx"
                from={a.x}
                to={b.x}
                dur="1.2s"
                begin={`${i * 0.08}s`}
                fill="freeze"
              />
              <animate
                attributeName="cy"
                from={a.y}
                to={b.y}
                dur="1.2s"
                begin={`${i * 0.08}s`}
                fill="freeze"
              />
              <animate
                attributeName="opacity"
                values="0;1;1;0"
                keyTimes="0;0.1;0.9;1"
                dur="1.2s"
                begin={`${i * 0.08}s`}
                fill="freeze"
              />
            </circle>
          );
        })}

        {/* seed background glow */}
        {(() => {
          const s = nodeById[activeSeed];
          return <circle cx={s.x} cy={s.y} r="48" fill="url(#seedGlow)" />;
        })()}

        {/* nodes */}
        {nodes.map((n) => {
          const t = tier(n.id);
          const isSeed = t === 0;
          const fill = isSeed ? '#0A0A0A' : '#FAFAF7';
          const textColor = isSeed ? '#FAFAF7' : t <= 1 ? '#0A0A0A' : '#8A8A83';
          const strokeColor = isSeed
            ? '#0A0A0A'
            : t === 1
            ? 'oklch(0.42 0.14 60)'
            : '#D4D3CC';
          const r = isSeed ? 22 : t === 1 ? 18 : 14;
          const opacity = t >= 3 ? 0.45 : 1;
          const labelOffset = r + 12;
          return (
            <g
              key={n.id}
              opacity={opacity}
              style={{ transition: 'opacity .6s' }}
            >
              <circle
                cx={n.x}
                cy={n.y}
                r={r}
                fill={fill}
                stroke={strokeColor}
                strokeWidth={isSeed ? 0 : t === 1 ? 1.5 : 1}
                style={{ transition: 'r .6s, stroke .6s' }}
              />
              {isSeed && (
                <circle
                  cx={n.x}
                  cy={n.y}
                  r={r}
                  fill="none"
                  stroke="oklch(0.72 0.14 60)"
                  strokeWidth="1.5"
                  opacity="0.7"
                >
                  <animate
                    attributeName="r"
                    from={r}
                    to={r + 20}
                    dur="1.6s"
                    repeatCount="indefinite"
                  />
                  <animate
                    attributeName="opacity"
                    from="0.6"
                    to="0"
                    dur="1.6s"
                    repeatCount="indefinite"
                  />
                </circle>
              )}
              <text
                x={n.x}
                y={n.y + labelOffset}
                textAnchor="middle"
                fontFamily="JetBrains Mono, monospace"
                fontSize="11"
                fontWeight={isSeed || t === 1 ? 600 : 400}
                fill={textColor}
                style={{ transition: 'fill .6s' }}
              >
                {n.label}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

export default TagGraph;
